import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentstackService from '../services/contentstack';
// localStorage removed - using pure Contentstack approach
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './ResumeBuilder.css';

const ResumeBuilder = ({ userInfo, setUserInfo }) => {
  const navigate = useNavigate();
  const resumePreviewRef = useRef(null);
  const [activeSection, setActiveSection] = useState('personal');
  const [newSkill, setNewSkill] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [resumeEntryUid, setResumeEntryUid] = useState(null);
  const [loadingResume, setLoadingResume] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);
  const [resumeData, setResumeData] = useState({
    personal: {
      name: '',
      email: '',
      phone: '',
      location: '',
      linkedin: '',
      github: '',
      website: '',
      profilePicture: null
    },
    professionalSummary: '',
    education: {
      highestQualification: '',
      institutionName: '',
      startDate: '',
      endDate: ''
    },
    workHistory: [
      {
        id: 1,
        companyName: '',
        jobTitle: '',
        employmentType: 'Full Time',
        startDate: '',
        endDate: '',
        present: false,
        responsibilities: ''
      }
    ],
    internships: [
      {
        id: 1,
        role: '',
        company: '',
        durationInMonths: '',
        responsibilities: '',
        certificate: null
      }
    ],
    projects: [
      {
        id: 1,
        projectName: '',
        projectLink: '',
        description: ''
      }
    ],
    skills: []
  });

  const sections = [
    { id: 'personal', label: 'Personal Info', icon: '👤' },
    { id: 'summary', label: 'Professional Summary', icon: '📝' },
    { id: 'education', label: 'Education', icon: '🎓' },
    { id: 'experience', label: 'Work History', icon: '💼' },
    { id: 'internships', label: 'Internships', icon: '🎯' },
    { id: 'projects', label: 'Projects', icon: '🚀' },
    { id: 'skills', label: 'Skills', icon: '⚡' }
  ];

  // Load resume data from Contentstack only (no localStorage)
  useEffect(() => {
    console.log('🔄 Loading resume data from Contentstack only...');
    
    const loadFromContentstack = async () => {
      setLoadingResume(true);
      console.log('📄 Resume Builder loaded - ready to create/edit resumes');
      setLoadingResume(false);
    };

    loadFromContentstack();
  }, []); // Load only once on mount

  const handleInputChange = (section, field, value, index = null) => {
    setResumeData(prev => {
      if (index !== null) {
        // For array items (workHistory, internships, projects)
        const newArray = [...prev[section]];
        newArray[index] = { ...newArray[index], [field]: value };
        return { ...prev, [section]: newArray };
      } else if (section === 'personal' || section === 'education') {
        // For object fields
        return {
          ...prev,
          [section]: { ...prev[section], [field]: value }
        };
      } else {
        // For simple fields like professionalSummary
        return { ...prev, [section]: value };
      }
    });
  };

  const handleFileChange = (section, field, file, index = null) => {
    setResumeData(prev => {
      if (index !== null) {
        const newArray = [...prev[section]];
        newArray[index] = { ...newArray[index], [field]: file };
        return { ...prev, [section]: newArray };
      } else {
        return {
          ...prev,
          [section]: { ...prev[section], [field]: file }
        };
      }
    });
  };

  const addWorkHistory = () => {
    console.log('🏢 Adding new work experience entry');
    const newWork = {
      id: Date.now(),
      companyName: '',
      jobTitle: '',
      employmentType: 'Full Time',
      startDate: '',
      endDate: '',
      present: false,
      responsibilities: ''
    };
    setResumeData(prev => ({
      ...prev,
      workHistory: [...prev.workHistory, newWork]
    }));
    console.log('✅ New work experience added');
  };

  const addInternship = () => {
    const newInternship = {
      id: Date.now(),
      role: '',
      company: '',
      durationInMonths: '',
      responsibilities: '',
      certificate: null
    };
    setResumeData(prev => ({
      ...prev,
      internships: [...prev.internships, newInternship]
    }));
  };

  const addProject = () => {
    const newProject = {
      id: Date.now(),
      projectName: '',
      projectLink: '',
      description: ''
    };
    setResumeData(prev => ({
      ...prev,
      projects: [...prev.projects, newProject]
    }));
  };

  const removeItem = (section, index) => {
    setResumeData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };


  // Save draft to localStorage only
  // Draft functionality removed - using pure Contentstack approach

  // Save/update resume to Contentstack and publish
  const handleUpdateResume = async () => {
    // Validation checks
    if (!resumeData.personal.name || !resumeData.personal.email || !resumeData.personal.phone) {
      setSaveStatus({ type: 'error', message: 'Please fill in your name, email, and phone number before saving.' });
      return;
    }

    // Validate phone number has at least 10 digits
    const phoneDigits = resumeData.personal.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setSaveStatus({ type: 'error', message: 'Phone number must contain at least 10 digits.' });
      return;
    }

    try {
      setSaving(true);
      setSaveStatus(null);

      // Only create subscriber entry if user opted in via checkbox
      if (subscribeToNewsletter && resumeData.personal.email) {
        console.log('📧 User opted in to newsletter - creating subscriber entry...');
        try {
          await ContentstackService.createSubscriber({
            name: resumeData.personal.name,
            email: resumeData.personal.email
          });
          console.log('✅ Subscriber created successfully');
        } catch (error) {
          console.log('ℹ️ Subscriber might already exist or failed to create:', error.message);
          // Don't block resume save if subscriber creation fails
        }
      }

      // Debug: Log the data being saved
      console.log('Saving resume data to Contentstack:', resumeData);

      let result;
      if (resumeEntryUid) {
        // Update existing resume
        console.log('Updating existing resume with UID:', resumeEntryUid);
        result = await ContentstackService.updateResume(resumeEntryUid, resumeData);
        console.log('Resume updated successfully:', result);
      } else {
        // Create new resume
        console.log('Creating new resume...');
        result = await ContentstackService.createResume(resumeData);
        console.log('Resume created successfully:', result);
        setResumeEntryUid(result.uid);
      }

      // Check if publishing was successful and show appropriate message
      if (result._published === false && result._publishError) {
        // Entry was saved but publishing failed
        setSaveStatus({ 
          type: 'warning', 
          message: `Resume saved but not published: ${result._publishError}. Please fix the errors and try again.`
        });
      } else if (result._published === true) {
        // Entry was saved and published successfully
        setSaveStatus({ 
          type: 'success', 
          message: resumeEntryUid ? 'Resume updated and published successfully!' : 'Resume saved and published successfully!'
        });
      } else {
        // Entry was saved (publishing status unknown)
        setSaveStatus({ 
          type: 'success', 
          message: resumeEntryUid ? 'Resume updated successfully!' : 'Resume saved successfully!'
        });
      }

      // localStorage removed - using pure Contentstack approach

      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(null), 5000);
    } catch (error) {
      console.error('Error saving resume:', error);
      
      // Parse Contentstack validation errors
      let errorMessage = 'Failed to save resume. Please check the following:';
      
      try {
        // Try to parse the error response
        if (error.message.includes('HTTP error') && error.message.includes('{')) {
          const errorStart = error.message.indexOf('{');
          const errorJson = error.message.substring(errorStart);
          const parsedError = JSON.parse(errorJson);
          
          if (parsedError.errors) {
            const fieldErrors = [];
            Object.keys(parsedError.errors).forEach(field => {
              const fieldName = field.replace(/_/g, ' ').replace(/\./g, ' → ');
              const errorDetails = Array.isArray(parsedError.errors[field]) 
                ? parsedError.errors[field].join(', ')
                : parsedError.errors[field];
              fieldErrors.push(`${fieldName}: ${errorDetails}`);
            });
            
            if (fieldErrors.length > 0) {
              errorMessage = `Validation errors:\n• ${fieldErrors.join('\n• ')}`;
            }
          } else if (parsedError.error_message) {
            errorMessage = parsedError.error_message;
          }
        } else if (error.message.includes('HTTP error')) {
          errorMessage = `Save failed: ${error.message}. Check your Contentstack configuration.`;
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your internet connection.';
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
        errorMessage = `Save failed: ${error.message}`;
      }
      
      setSaveStatus({ 
        type: 'error', 
        message: errorMessage
      });
      setTimeout(() => setSaveStatus(null), 5000); // 5 second timeout for all notifications
    } finally {
      setSaving(false);
    }
  };

  // Refresh data from Contentstack
  const handleRefreshData = async () => {
    // Use email from form if user is not logged in
    const emailToUse = userInfo?.email || resumeData.personal.email;
    
    if (!emailToUse) {
      setSaveStatus({ type: 'error', message: 'Please enter your email address first to sync data.' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    try {
      setSaveStatus({ type: 'loading', message: '🔄 Syncing latest changes from Contentstack CMS...' });
      
      // Fetch latest resume from Contentstack
      console.log('🔍 Fetching latest resume for email:', emailToUse);
      const latestResume = await ContentstackService.getLatestUserResume(emailToUse);
      console.log('📦 Received resume data:', latestResume);
      
      if (latestResume) {
        const transformedData = ContentstackService.transformFromContentstack(latestResume);
        if (transformedData) {
          // Store current data for comparison
          const hasChanges = JSON.stringify(resumeData) !== JSON.stringify(transformedData);
          
          setResumeData(transformedData);
          setResumeEntryUid(latestResume.uid);
          
          if (hasChanges) {
            setSaveStatus({ 
              type: 'success', 
              message: '✅ Form updated with latest changes from Contentstack CMS!' 
            });
          } else {
            setSaveStatus({ 
              type: 'info', 
              message: 'ℹ️ Form is already up-to-date with Contentstack CMS.' 
            });
          }
        } else {
          setSaveStatus({ type: 'error', message: 'Failed to transform Contentstack data.' });
        }
      } else {
        setSaveStatus({ type: 'error', message: 'No resume found in Contentstack for your email.' });
      }
      
      setTimeout(() => setSaveStatus(null), 5000);
    } catch (error) {
      console.error('Error refreshing data:', error);
      setSaveStatus({ type: 'error', message: 'Failed to refresh data from Contentstack.' });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleDownloadPDF = async () => {
    if (!resumePreviewRef.current) {
      setSaveStatus({ type: 'error', message: 'Resume preview not available for download.' });
      setTimeout(() => setSaveStatus(null), 5000);
      return;
    }

    try {
      setSaveStatus({ type: 'success', message: 'Generating PDF... Please wait.' });
      
      // Create a temporary container for PDF generation with better styling
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.minHeight = '297mm'; // A4 height
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20mm';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      
      // Clone the resume content
      const resumeContent = resumePreviewRef.current.cloneNode(true);
      tempContainer.appendChild(resumeContent);
      document.body.appendChild(tempContainer);

      // Generate canvas from the temporary container
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794, // A4 width in pixels at 96 DPI
        height: 1123 // A4 height in pixels at 96 DPI
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit A4
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20; // 10mm margin on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add image to PDF
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pdfHeight - 20));
      
      // Generate filename
      const fileName = resumeData.personal.name 
        ? `${resumeData.personal.name.replace(/\s+/g, '_')}_Resume.pdf`
        : 'Resume.pdf';
      
      // Download PDF
      pdf.save(fileName);
      
      setSaveStatus({ type: 'success', message: 'Resume downloaded successfully!' });
      setTimeout(() => setSaveStatus(null), 5000);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSaveStatus({ type: 'error', message: 'Failed to generate PDF. Please try again.' });
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };



  const renderPersonalInfo = () => (
    <div className="section-content">
      <h3>Personal Information</h3>
      
      {/* User Data Status - removed localStorage dependency */}
      
      {/* Profile Picture Section */}
      <div className="profile-picture-section">
        <div className="profile-picture-container">
          {resumeData.personal.profilePicture ? (
            <img 
              src={typeof resumeData.personal.profilePicture === 'string' 
                ? resumeData.personal.profilePicture 
                : URL.createObjectURL(resumeData.personal.profilePicture)} 
              alt="Profile" 
              className="profile-picture-preview"
            />
          ) : (
            <div className="profile-picture-placeholder">
              <span>📷</span>
              <p>Upload Photo</p>
            </div>
          )}
          <input
            type="file"
            id="profilePicture"
            accept="image/*"
            onChange={(e) => handleFileChange('personal', 'profilePicture', e.target.files[0])}
            className="file-input"
          />
          <label 
            htmlFor="profilePicture" 
            className="btn btn-secondary btn-small"
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '0.5rem'
            }}
          >
            {resumeData.personal.profilePicture ? 'Change Photo' : 'Upload Photo'}
          </label>
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input
            type="text"
            className="form-input"
            value={resumeData.personal.name}
            onChange={(e) => handleInputChange('personal', 'name', e.target.value)}
            placeholder="Your full name"
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input
            type="email"
            className="form-input"
            value={resumeData.personal.email}
            onChange={(e) => handleInputChange('personal', 'email', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number *</label>
          <input
            type="tel"
            className="form-input"
            value={resumeData.personal.phone}
            onChange={(e) => handleInputChange('personal', 'phone', e.target.value)}
            placeholder="1234567890 (10 digits only)"
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Location</label>
          <input
            type="text"
            className="form-input"
            value={resumeData.personal.location}
            onChange={(e) => handleInputChange('personal', 'location', e.target.value)}
            placeholder="City, State/Country"
          />
        </div>
        <div className="form-group">
          <label className="form-label">LinkedIn Profile</label>
          <input
            type="url"
            className="form-input"
            value={resumeData.personal.linkedin}
            onChange={(e) => handleInputChange('personal', 'linkedin', e.target.value)}
            placeholder="https://linkedin.com/in/yourprofile"
          />
        </div>
        <div className="form-group">
          <label className="form-label">GitHub Profile</label>
          <input
            type="url"
            className="form-input"
            value={resumeData.personal.github}
            onChange={(e) => handleInputChange('personal', 'github', e.target.value)}
            placeholder="https://github.com/yourusername"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Personal Website</label>
          <input
            type="url"
            className="form-input"
            value={resumeData.personal.website}
            onChange={(e) => handleInputChange('personal', 'website', e.target.value)}
            placeholder="https://yourwebsite.com"
          />
        </div>
      </div>

      {/* Newsletter Subscription Checkbox */}
      <div className="form-group" style={{ marginTop: '20px' }}>
        <label className="checkbox-label" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#374151'
        }}>
          <input
            type="checkbox"
            checked={subscribeToNewsletter}
            onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <span>📧 Subscribe to our newsletter for career tips and updates</span>
        </label>
      </div>
    </div>
  );

  const renderSummary = () => (
    <div className="section-content">
      <h3>Professional Summary</h3>
      <div className="form-group">
        <label className="form-label">Professional Summary</label>
        <textarea
          className="form-textarea"
          rows="8"
          value={resumeData.professionalSummary}
          onChange={(e) => handleInputChange('professionalSummary', null, e.target.value)}
          placeholder="Write a compelling professional summary that highlights your key achievements, skills, and career objectives. This should be 3-4 sentences that capture your professional identity and value proposition..."
        />
        <div className="form-help">
          <p>💡 <strong>Tip:</strong> Include your years of experience, key skills, and what makes you unique in your field.</p>
        </div>
      </div>
    </div>
  );

  const renderEducation = () => (
    <div className="section-content">
      <h3>Education</h3>
      <div className="education-form card">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Highest Educational Qualification</label>
            <input
              type="text"
              className="form-input"
              value={resumeData.education.highestQualification}
              onChange={(e) => handleInputChange('education', 'highestQualification', e.target.value)}
              placeholder="e.g., Bachelor's Degree, Master's Degree, PhD"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Institution Name</label>
            <input
              type="text"
              className="form-input"
              value={resumeData.education.institutionName}
              onChange={(e) => handleInputChange('education', 'institutionName', e.target.value)}
              placeholder="College/University name"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input
              type="month"
              className="form-input"
              value={resumeData.education.startDate}
              onChange={(e) => handleInputChange('education', 'startDate', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input
              type="month"
              className="form-input"
              value={resumeData.education.endDate}
              onChange={(e) => handleInputChange('education', 'endDate', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderExperience = () => (
    <div className="section-content">
      <div className="section-header">
        <h3>Work History</h3>
        <button 
          className="btn btn-secondary btn-small" 
          onClick={addWorkHistory}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: '2px solid #3b82f6',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ➕ Add Company/Organization
        </button>
      </div>
      {resumeData.workHistory.map((work, index) => (
        <div key={work.id} className="work-item card">
          <div className="item-header">
            <h4>Work Experience {index + 1}</h4>
            {resumeData.workHistory.length > 1 && (
              <button 
                className="btn-remove" 
                onClick={() => removeItem('workHistory', index)}
                title="Remove this experience"
              >
                ×
              </button>
            )}
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                className="form-input"
                value={work.companyName}
                onChange={(e) => handleInputChange('workHistory', 'companyName', e.target.value, index)}
                placeholder="Company Name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Job Title</label>
              <input
                type="text"
                className="form-input"
                value={work.jobTitle}
                onChange={(e) => handleInputChange('workHistory', 'jobTitle', e.target.value, index)}
                placeholder="Your Job Title"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Employment Type</label>
              <select
                className="form-input"
                value={work.employmentType}
                onChange={(e) => handleInputChange('workHistory', 'employmentType', e.target.value, index)}
              >
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
                <option value="Contract">Contract</option>
                <option value="Freelance">Freelance</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="month"
                className="form-input"
                value={work.startDate}
                onChange={(e) => handleInputChange('workHistory', 'startDate', e.target.value, index)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="month"
                className="form-input"
                value={work.endDate}
                onChange={(e) => handleInputChange('workHistory', 'endDate', e.target.value, index)}
                disabled={work.present}
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={work.present}
                  onChange={(e) => handleInputChange('workHistory', 'present', e.target.checked, index)}
                />
                Currently working here
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Responsibilities & Achievements</label>
            <textarea
              className="form-textarea"
              rows="5"
              value={work.responsibilities}
              onChange={(e) => handleInputChange('workHistory', 'responsibilities', e.target.value, index)}
              placeholder="Describe your key responsibilities, achievements, and impact in this role..."
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderInternships = () => (
    <div className="section-content">
      <div className="section-header">
        <h3>Internships</h3>
        <button className="btn btn-secondary btn-small" onClick={addInternship}>
          + Add Internship
        </button>
      </div>
      {resumeData.internships.map((internship, index) => (
        <div key={internship.id} className="internship-item card">
          <div className="item-header">
            <h4>Internship {index + 1}</h4>
            {resumeData.internships.length > 1 && (
              <button 
                className="btn-remove" 
                onClick={() => removeItem('internships', index)}
                title="Remove this internship"
              >
                ×
              </button>
            )}
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Role/Position</label>
              <input
                type="text"
                className="form-input"
                value={internship.role}
                onChange={(e) => handleInputChange('internships', 'role', e.target.value, index)}
                placeholder="Intern Position Title"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Company</label>
              <input
                type="text"
                className="form-input"
                value={internship.company}
                onChange={(e) => handleInputChange('internships', 'company', e.target.value, index)}
                placeholder="Company Name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Duration (in months)</label>
              <input
                type="number"
                className="form-input"
                value={internship.durationInMonths}
                onChange={(e) => handleInputChange('internships', 'durationInMonths', e.target.value, index)}
                placeholder="e.g., 3"
                min="1"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Responsibilities & Learning</label>
            <textarea
              className="form-textarea"
              rows="4"
              value={internship.responsibilities}
              onChange={(e) => handleInputChange('internships', 'responsibilities', e.target.value, index)}
              placeholder="Describe your responsibilities, projects worked on, and key learnings from this internship..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Certificate (Optional)</label>
            <div className="file-upload-container">
              <input
                type="file"
                id={`certificate-${internship.id}`}
                className="file-input"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('internships', 'certificate', e.target.files[0], index)}
                style={{ display: 'none' }}
              />
              <label 
                htmlFor={`certificate-${internship.id}`}
                className="btn btn-secondary btn-small"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                📎 {internship.certificate ? 'Change Certificate' : 'Upload Certificate'}
              </label>
              {internship.certificate && (
                <div className="file-preview" style={{ marginTop: '0.5rem' }}>
                  <span>📄 {internship.certificate.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderProjects = () => (
    <div className="section-content">
      <div className="section-header">
        <h3>Projects</h3>
        <button className="btn btn-secondary btn-small" onClick={addProject}>
          + Add Project
        </button>
      </div>
      {resumeData.projects.map((project, index) => (
        <div key={project.id} className="project-item card">
          <div className="item-header">
            <h4>Project {index + 1}</h4>
            {resumeData.projects.length > 1 && (
              <button 
                className="btn-remove" 
                onClick={() => removeItem('projects', index)}
                title="Remove this project"
              >
                ×
              </button>
            )}
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Project Name</label>
              <input
                type="text"
                className="form-input"
                value={project.projectName}
                onChange={(e) => handleInputChange('projects', 'projectName', e.target.value, index)}
                placeholder="Your Project Name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Project Link (Optional)</label>
              <input
                type="url"
                className="form-input"
                value={project.projectLink}
                onChange={(e) => handleInputChange('projects', 'projectLink', e.target.value, index)}
                placeholder="https://github.com/username/project or live demo link"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Project Description</label>
            <textarea
              className="form-textarea"
              rows="5"
              value={project.description}
              onChange={(e) => handleInputChange('projects', 'description', e.target.value, index)}
              placeholder="Describe the project, technologies used, your role, and key features or achievements..."
            />
          </div>
        </div>
      ))}
    </div>
  );

  const addSkill = () => {
    if (newSkill.trim()) {
      // Check if skill already exists (handle both string and object formats)
      const skillExists = resumeData.skills.some(skill => {
        const skillName = typeof skill === 'string' ? skill : skill.name;
        return skillName.toLowerCase() === newSkill.trim().toLowerCase();
      });
      
      if (!skillExists) {
        setResumeData(prev => ({
          ...prev,
          skills: [...prev.skills, { name: newSkill.trim(), rating: 0 }]
        }));
        setNewSkill('');
      }
    }
  };

  const removeSkill = (index) => {
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const updateSkillRating = (skillIndex, rating) => {
    setResumeData(prev => {
      const updatedSkills = [...prev.skills];
      if (typeof updatedSkills[skillIndex] === 'string') {
        // Convert string skill to object with rating
        updatedSkills[skillIndex] = {
          name: updatedSkills[skillIndex],
          rating: rating
        };
      } else {
        // Update existing object skill
        updatedSkills[skillIndex] = {
          ...updatedSkills[skillIndex],
          rating: rating
        };
      }
      return { ...prev, skills: updatedSkills };
    });
  };

  const renderSkills = () => (
    <div className="section-content">
      <h3>Skills</h3>
      <div className="skills-input-section">
        <div className="skill-input-group">
          <input
            type="text"
            className="form-input"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            placeholder="Enter a skill (e.g., JavaScript, Project Management)"
            onKeyPress={(e) => e.key === 'Enter' && addSkill()}
          />
          <button 
            type="button" 
            className="btn btn-primary btn-small" 
            onClick={addSkill}
            disabled={!newSkill.trim()}
          >
            Add Skill
          </button>
        </div>
        <div className="form-help">
          <p>💡 <strong>Tip:</strong> Add skills and rate your proficiency level.</p>
        </div>
      </div>
      
      {resumeData.skills.length > 0 && (
        <div className="skills-display">
          <h4>Your Skills:</h4>
          <div className="skills-list">
            {resumeData.skills.map((skill, index) => (
              <div key={index} className="skill-item">
                <div className="skill-info">
                  <span className="skill-name">{typeof skill === 'string' ? skill : skill.name}</span>
                </div>
                <div className="skill-rating">
                  <label className="rating-label">Proficiency:</label>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star ${(typeof skill === 'object' ? skill.rating : 0) >= star ? 'active' : ''}`}
                        onClick={() => updateSkillRating(index, star)}
                        title={`${star} star${star > 1 ? 's' : ''}`}
                      >
                        ⭐
                      </button>
                    ))}
                  </div>
                  <span className="rating-text">
                    {typeof skill === 'object' && skill.rating ? `${skill.rating}/5` : 'Not rated'}
                  </span>
                  <button 
                    type="button"
                    className="skill-remove"
                    onClick={() => removeSkill(index)}
                    title="Remove skill"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {resumeData.skills.length === 0 && (
        <div className="empty-state">
          <p>No skills added yet. Start by adding your key technical and soft skills!</p>
        </div>
      )}
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'personal':
        return renderPersonalInfo();
      case 'summary':
        return renderSummary();
      case 'education':
        return renderEducation();
      case 'experience':
        return renderExperience();
      case 'internships':
        return renderInternships();
      case 'projects':
        return renderProjects();
      case 'skills':
        return renderSkills();
      default:
        return renderPersonalInfo();
    }
  };

  // Show loading indicator while resume data is being loaded
  if (loadingResume) {
    return (
      <div className="resume-builder">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>Loading your resume...</h2>
          <p>Please wait while we retrieve your saved data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resume-builder">
      {/* Header */}
      <header className="builder-header">
        <div className="header-content">
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <div className="header-center">
            <h1>Resume Builder</h1>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-primary" 
              onClick={handleUpdateResume}
              disabled={saving}
              style={{ 
                backgroundColor: '#667eea', 
                color: 'white', 
                border: '2px solid #667eea',
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : (resumeEntryUid ? 'Update Resume' : 'Save Resume')}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowPreviewModal(true)}
              style={{ 
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                color: 'white', 
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginLeft: '8px'
              }}
            >
              👁️ Preview
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleRefreshData}
              style={{ 
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                color: 'white', 
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginLeft: '8px'
              }}
            >
              🔄 Refresh Data
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleDownloadPDF}
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: 'white', 
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginLeft: '8px'
              }}
            >
              📄 Download PDF
            </button>
          </div>
        </div>
      </header>

      <div className="builder-container">
        {/* Sidebar */}
        <aside className="builder-sidebar">
          <nav className="section-nav">
            {sections.map(section => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content - Now Full Width */}
        <main className="builder-main-full">
          <div className="form-container">
            {renderSection()}
          </div>
        </main>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Resume Preview</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowPreviewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="professional-resume-template" ref={resumePreviewRef}>
                
                {/* Header Section */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '25px', marginBottom: '30px', paddingBottom: '20px', borderBottom: '3px solid #2563eb' }}>
                  {/* Profile Picture */}
                  {resumeData.personal.profilePicture && (
                    <div style={{ flexShrink: 0 }}>
                      <img 
                        src={typeof resumeData.personal.profilePicture === 'string' 
                          ? resumeData.personal.profilePicture 
                          : URL.createObjectURL(resumeData.personal.profilePicture)} 
                        alt="Profile" 
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '4px solid #2563eb',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Header Content */}
                  <div style={{ flex: 1 }}>
                    <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 10px 0' }}>
                      {resumeData.personal.name || 'Your Name'}
                    </h1>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '14px', color: '#64748b' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        📧 {resumeData.personal.email || 'your.email@example.com'}
                      </span>
                      {resumeData.personal.phone && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          📱 {resumeData.personal.phone}
                        </span>
                      )}
                      {resumeData.personal.location && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          📍 {resumeData.personal.location}
                        </span>
                      )}
                      {resumeData.personal.linkedin && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          💼 LinkedIn
                        </span>
                      )}
                      {resumeData.personal.github && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          🐙 GitHub
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Professional Summary */}
                {resumeData.professionalSummary && resumeData.professionalSummary.trim() && (
                  <div style={{ marginBottom: '25px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                      PROFESSIONAL SUMMARY
                    </h2>
                    <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
                      {resumeData.professionalSummary}
                    </p>
                  </div>
                )}

                {/* === WORK EXPERIENCE === */}
                {resumeData.workHistory && Array.isArray(resumeData.workHistory) && resumeData.workHistory.filter(w => w.companyName || w.jobTitle).length > 0 && (
                  <div style={{ marginBottom: '25px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                      WORK EXPERIENCE
                    </h2>
                    {resumeData.workHistory.filter(work => work.companyName || work.jobTitle).map((work, idx) => (
                      <div key={idx} style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 5px 0' }}>
                          {work.jobTitle || 'Job Title'}
                        </h3>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600' }}>{work.companyName}</span>
                          {work.employmentType && <span> • {work.employmentType}</span>}
                          {work.startDate && (
                            <span style={{ marginLeft: '10px' }}>
                              {new Date(work.startDate + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                              {' - '}
                              {work.present ? 'Present' : (work.endDate ? new Date(work.endDate + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : 'Present')}
                            </span>
                          )}
                        </div>
                        {work.responsibilities && (
                          <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569', margin: '0' }}>
                            {work.responsibilities}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* === EDUCATION === */}
                {resumeData.education && (resumeData.education.institutionName || resumeData.education.highestQualification) && (
                  <div style={{ marginBottom: '25px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                      EDUCATION
                    </h2>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 5px 0' }}>
                        {resumeData.education.highestQualification || 'Degree'}
                      </h3>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '600' }}>{resumeData.education.institutionName}</span>
                        {resumeData.education.startDate && (
                          <span style={{ marginLeft: '10px' }}>
                            {new Date(resumeData.education.startDate + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                            {resumeData.education.endDate && ' - ' + new Date(resumeData.education.endDate + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* === PROJECTS === */}
                {resumeData.projects && Array.isArray(resumeData.projects) && resumeData.projects.filter(p => p.projectName).length > 0 && (
                  <div style={{ marginBottom: '25px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                      PROJECTS
                    </h2>
                    {resumeData.projects.filter(project => project.projectName).map((project, idx) => (
                      <div key={idx} style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 5px 0' }}>
                          {project.projectName}
                        </h3>
                        {project.projectLink && (
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>
                            🔗 <a href={project.projectLink} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{project.projectLink}</a>
                          </div>
                        )}
                        {project.description && (
                          <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569', margin: '0' }}>
                            {project.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* === INTERNSHIPS === */}
                {resumeData.internships && Array.isArray(resumeData.internships) && resumeData.internships.filter(i => i.company || i.role).length > 0 && (
                  <div style={{ marginBottom: '25px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                      INTERNSHIPS
                    </h2>
                    {resumeData.internships.filter(internship => internship.company || internship.role).map((internship, idx) => (
                      <div key={idx} style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 5px 0' }}>
                          {internship.role || 'Intern'}
                        </h3>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600' }}>{internship.company}</span>
                          {internship.durationInMonths && <span> • {internship.durationInMonths} months</span>}
                        </div>
                        {internship.responsibilities && (
                          <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569', margin: '0' }}>
                            {internship.responsibilities}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* === SKILLS === */}
                {resumeData.skills && Array.isArray(resumeData.skills) && resumeData.skills.length > 0 && (
                  <div style={{ marginBottom: '25px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                      SKILLS
                    </h2>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {resumeData.skills.map((skill, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 15px', background: '#f1f5f9', borderRadius: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                            {typeof skill === 'string' ? skill : skill.name}
                          </span>
                          {typeof skill === 'object' && skill.rating && (
                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                              {'⭐'.repeat(skill.rating)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-primary" 
                onClick={handleDownloadPDF}
                style={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                  color: 'white', 
                  border: 'none',
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginRight: '12px'
                }}
              >
                📄 Download PDF
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowPreviewModal(false)}
                style={{ 
                  background: '#f3f4f6', 
                  color: '#374151', 
                  border: '2px solid #d1d5db',
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Notification */}
      {saveStatus && (
        <div className={`floating-notification ${saveStatus.type}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {saveStatus.type === 'success' && '✅'}
              {saveStatus.type === 'error' && '❌'}
              {saveStatus.type === 'warning' && '⚠️'}
            </span>
            <span className="notification-message">{saveStatus.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeBuilder;



